// Catalog.ID Client — Supersecret Multi-line Textarea
// ============================================================================
// Shared component for the five places that prompt for a supersecret:
//   - key-grant/supersecret-prompt.ts   (save flow)
//   - auth/login-view.ts                (new-device unlock)
//   - auth/key-rotation-view.ts         (rotation flow)
//   - pane-types/agents-list-pane.ts    (agent creation)
//   - pane-types/org-workspace-actions.ts (org rotate / flatten)
//
// The supersecret format is four 12-character lines (11 random + 1 per-line
// checksum, base57). The point of the per-line checksum is line-level error
// reporting during manual entry — a single-line `<input type="password">`
// loses both the line structure and the per-line feedback. This helper
// centralises the textarea + live checksum validation so all five sites
// behave identically.

import { createElement } from '@catalog-shell';
import { parseSupersecret } from '../crypto/supersecret';

export interface SupersecretTextareaResult {
    /** Wrapper element containing the textarea + checksum status row. */
    element: HTMLElement;
    /** The underlying textarea — exposed for focus / select / e2e. */
    textarea: HTMLTextAreaElement;
    /** Current trimmed value (raw — caller passes to deriveKeyFromSupersecret). */
    getValue(): string;
    /**
     * True iff value is non-empty AND parses with zero checksum errors.
     * Empty input is invalid: a disabled submit gate that allows empty
     * strings is no gate at all.
     */
    isValid(): boolean;
    /** Subscribe to validity transitions. Fires on every `input` event when validity flips. */
    onValidityChange(cb: (valid: boolean) => void): void;
    /** Force re-validation (e.g., after programmatic value change). */
    refresh(): void;
}

export interface SupersecretTextareaOptions {
    placeholder?: string;
    /** id on the textarea — required for existing e2e selectors like `#cid-supersecret`. */
    id?: string;
    /** Set to true to focus the textarea on next tick. */
    autoFocus?: boolean;
}

export function createSupersecretTextarea(opts: SupersecretTextareaOptions = {}): SupersecretTextareaResult {
    const wrapper = createElement('div', { className: 'cid-auth-field' });

    const textarea = createElement('textarea', {
        className: 'cid-auth-input cid-auth-input--multiline',
        attributes: {
            placeholder: opts.placeholder ?? 'Enter your supersecret (4 lines)',
            autocomplete: 'off',
            rows: '4',
            spellcheck: 'false',
        },
    }) as HTMLTextAreaElement;
    if (opts.id) textarea.id = opts.id;
    wrapper.appendChild(textarea);

    // Status line: either empty (clean), or "Checksum error on line N[, M]".
    // Reserves vertical space so the form doesn't jump on validation flips.
    const status = createElement('div', { className: 'cid-supersecret-status' });
    wrapper.appendChild(status);

    let lastValid: boolean | null = null;
    const validityListeners: ((valid: boolean) => void)[] = [];

    function compute(): boolean {
        const val = textarea.value.trim();
        if (!val) {
            status.textContent = '';
            status.classList.remove('cid-supersecret-status--error');
            return false;
        }
        const { lineErrors } = parseSupersecret(val);
        if (lineErrors.length === 0) {
            status.textContent = '';
            status.classList.remove('cid-supersecret-status--error');
            return true;
        }
        status.textContent = `Checksum error on line ${lineErrors.join(', ')}`;
        status.classList.add('cid-supersecret-status--error');
        return false;
    }

    function refresh(): void {
        const valid = compute();
        if (valid !== lastValid) {
            lastValid = valid;
            for (const cb of validityListeners) cb(valid);
        }
    }

    textarea.addEventListener('input', refresh);

    if (opts.autoFocus) {
        setTimeout(() => textarea.focus(), 0);
    }

    return {
        element: wrapper,
        textarea,
        getValue: () => textarea.value.trim(),
        isValid: () => lastValid === true,
        onValidityChange: (cb) => { validityListeners.push(cb); },
        refresh,
    };
}
